import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { User, Building2, Bell, CreditCard, ShieldCheck, Plug, BarChart3, Users, Briefcase } from "lucide-react"
import { CLINICAL_VERTICALS, labelForVertical, setSessionVertical } from "@/config/clinical-verticals"
import axios from "axios"
import AppLayout from "@/components/app/AppLayout"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { API_BASE } from "@/config/api"
import { toast } from "sonner"

const API = API_BASE


export default function Settings() {
  const { t } = useTranslation()
  const tabs = [
    { id: "profile", label: t("app.settings.tabs.profile"), icon: User },
    { id: "practice", label: t("app.settings.tabs.practice"), icon: Building2 },
    { id: "employer", label: t("app.settings.tabs.employer"), icon: Briefcase },
    { id: "notifications", label: t("app.settings.tabs.notifications"), icon: Bell },
    { id: "billing", label: t("app.settings.tabs.billing"), icon: CreditCard },
    { id: "security", label: t("app.settings.tabs.security"), icon: ShieldCheck },
    { id: "integrations", label: t("app.settings.tabs.integrations"), icon: Plug },
  ]
  const [active, setActive] = useState("profile")
  const [settings, setSettings] = useState(null)
  const [integrations, setIntegrations] = useState([])
  const [audit, setAudit] = useState([])
  const [analytics, setAnalytics] = useState(null)
  const [compliance, setCompliance] = useState([])
  const [invite, setInvite] = useState({ email: "", name: "", role: "practitioner" })
  const [branding, setBranding] = useState({ logo_url: "", primary_color: "#0d9488" })
  const [totpUri, setTotpUri] = useState("")
  const [totpSecret, setTotpSecret] = useState("")
  const [totpCode, setTotpCode] = useState("")
  const [backupCodes, setBackupCodes] = useState([])
  const [searchParams] = useSearchParams()
  const [fiscalMonth, setFiscalMonth] = useState(() => new Date().toISOString().slice(0, 7))
  const [fiscal, setFiscal] = useState(null)
  const [teleProvider, setTeleProvider] = useState("jitsi")
  const [teleUrl, setTeleUrl] = useState("")
  const [clinicalDraft, setClinicalDraft] = useState(null)
  const [clinicalSaving, setClinicalSaving] = useState(false)
  const [billingInterval, setBillingInterval] = useState("monthly")
  const [saasInvoices, setSaasInvoices] = useState([])
  const [employer, setEmployer] = useState({
    legal_form: "srl",
    caen: "",
    delegated_person: "",
    itm_registration: "",
    payroll_defaults: {},
  })

  const load = () => {
    axios.get(`${API}/settings`).then((r) => {
      setSettings(r.data)
      const c = r.data.clinical || {}
      setClinicalDraft({
        primary_vertical: c.primary_vertical || "psych",
        enabled_verticals: [...(c.enabled_verticals || ["psych"])],
        clinical_specialty: r.data.profile?.clinical_specialty || "",
      })
      setBranding({
        logo_url: r.data.practice?.logo_url || "",
        primary_color: r.data.practice?.primary_color || "#0d9488",
      })
      const th = r.data.telehealth || {}
      setTeleProvider(th.provider || "jitsi")
      setTeleUrl(th.room_base_url || "")
    }).catch(() => {})
    axios.get(`${API}/integrations`).then((r) => setIntegrations(r.data)).catch(() => {})
  }

  useEffect(() => { load() }, [])
  useEffect(() => {
    axios.get(`${API}/hr/employer-profile`).then((r) => {
      if (r.data?.employer_profile) setEmployer((e) => ({ ...e, ...r.data.employer_profile }))
    }).catch(() => {})
  }, [])
  useEffect(() => {
    if (searchParams.get("gcal") === "connected") toast.success(t("app.settings.toast.gcalConnected"))
  }, [searchParams])

  useEffect(() => {
    const tab = searchParams.get("tab")
    if (tab === "clinical") setActive("profile")
    else if (tab && tabs.some((item) => item.id === tab)) setActive(tab)
  }, [searchParams])

  useEffect(() => {
    if (active !== "profile") return
    const t = setTimeout(() => {
      if (window.location.hash === "#clinical-specialty") {
        document.getElementById("clinical-specialty")?.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 200)
    return () => clearTimeout(t)
  }, [active, clinicalDraft])

  const save = async (patch) => {
    try {
      await axios.patch(`${API}/settings`, patch)
      toast.success(t("app.settings.toast.saved"))
      load()
    } catch {
      toast.error(t("app.settings.toast.saveFailed"))
    }
  }

  const connect = async (provider, connectable, statusHint) => {
    if (provider === "google_calendar") {
      try {
        const { data } = await axios.get(`${API}/integrations/google_calendar/auth-url`)
        if (data.url) window.location.href = data.url
        else toast.error(data.message || "Configurează GOOGLE_CLIENT_ID")
      } catch {
        toast.error("Google Calendar indisponibil")
      }
      return
    }
    if (!connectable) {
      toast.info(statusHint || "Integrare neconfigurată — vezi documentația de setup")
      return
    }
    try {
      await axios.post(`${API}/integrations/${provider}/connect`)
      toast.success("Conectat")
      load()
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conexiune eșuată")
    }
  }

  const stripeCheckout = async (plan = "practice") => {
    try {
      const sub = settings?.subscription || {}
      const addon_ids =
        plan === "clinic" ? (sub.addon_ids || []).filter((a) => a !== "predictive_ai") : []
      const { data } = await axios.post(`${API}/stripe/subscription-checkout`, {
        plan,
        addon_ids,
        interval: billingInterval,
      })
      if (data.url) window.location.href = data.url
      else toast.info(data.message || "Stripe neconfigurat — planul se aplică la activare chei.")
    } catch {
      toast.error("Checkout eșuat")
    }
  }

  const loadSaasInvoices = async () => {
    try {
      const { data } = await axios.get(`${API}/stripe/subscription-invoices`)
      setSaasInvoices(data.invoices || [])
    } catch {
      toast.error("Facturi abonament indisponibile")
    }
  }

  const billingPortal = async () => {
    try {
      const { data } = await axios.post(`${API}/stripe/billing-portal`)
      if (data.url) window.location.href = data.url
      else toast.info("Portal Stripe neconfigurat")
    } catch {
      toast.error("Portal indisponibil")
    }
  }

  const runReminders = async () => {
    try {
      const { data } = await axios.post(`${API}/reminders/run`)
      const total = (data.sent_email || 0) + (data.sent_sms || 0)
      toast.success(
        `Trimise: ${total} (email: ${data.sent_email || 0}, SMS: ${data.sent_sms || 0}; SMTP: ${data.smtp_enabled ? "da" : "nu"})`,
      )
    } catch {
      toast.error(t("app.settings.toast.reminderFailed"))
    }
  }

  const loadAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API}/analytics/practice`)
      setAnalytics(data)
      setActive("practice")
    } catch {
      toast.error("Analytics indisponibil")
    }
  }

  const loadCompliance = async () => {
    try {
      const { data } = await axios.get(`${API}/compliance/checklist`)
      setCompliance(data.items || [])
      setActive("security")
    } catch {
      toast.error("Checklist indisponibil")
    }
  }

  const sendInvite = async () => {
    const used = subscription.seat_used ?? 0
    const limit = subscription.seat_limit ?? 1
    if (used >= limit) {
      toast.error("Limita de locuri atinsă — upgrade planul pentru a invita colegi")
      setActive("billing")
      return
    }
    try {
      const { data } = await axios.post(`${API}/team/invite`, invite)
      await navigator.clipboard.writeText(data.invite_link)
      toast.success("Link invitație copiat")
    } catch (e) {
      const detail = e.response?.data?.detail
      if (detail?.code === "seat_limit_reached") {
        toast.error(`Locuri ocupate: ${detail.seat_used}/${detail.seat_limit}`)
        setActive("billing")
      } else if (detail?.code === "upgrade_required") {
        toast.error("Invitațiile necesită plan Practice+")
        setActive("billing")
      } else {
        toast.error(typeof detail === "string" ? detail : "Invitație eșuată")
      }
    }
  }

  const saveBranding = async () => {
    try {
      await axios.patch(`${API}/practices/branding`, branding)
      toast.success("Branding salvat")
    } catch {
      toast.error("Branding eșuat")
    }
  }

  const exportData = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/export`)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "practice-export.json"
      a.click()
      toast.success("Export GDPR descărcat")
    } catch {
      toast.error("Export eșuat")
    }
  }

  const exportFull = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/export/full`)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `gdpr-full-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      toast.success("Pachet complet descărcat")
    } catch {
      toast.error("Export complet eșuat (necesită rol owner/admin)")
    }
  }

  const setup2fa = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`)
      setTotpSecret(data.secret)
      setTotpUri(data.otpauth_uri)
      toast.success("Scanează URI-ul în aplicația de autentificare")
    } catch {
      toast.error("Setup 2FA eșuat")
    }
  }

  const verify2fa = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify`, { code: totpCode })
      setBackupCodes(data.backup_codes || [])
      setTotpCode("")
      toast.success("2FA activat")
      load()
    } catch {
      toast.error("Cod invalid")
    }
  }

  const disable2fa = async () => {
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: totpCode })
      setTotpUri("")
      setTotpSecret("")
      setBackupCodes([])
      setTotpCode("")
      toast.success("2FA dezactivat")
      load()
    } catch {
      toast.error("Cod invalid")
    }
  }

  const uploadLogo = async (file) => {
    if (!file) return
    const fd = new FormData()
    fd.append("file", file)
    try {
      const { data } = await axios.post(`${API}/practices/logo/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      setBranding((b) => ({ ...b, logo_url: data.logo_url }))
      toast.success("Logo încărcat")
      load()
    } catch {
      toast.error("Upload logo eșuat")
    }
  }

  const loadFiscal = async () => {
    try {
      const { data } = await axios.get(`${API}/reports/fiscal-summary`, { params: { month: fiscalMonth } })
      setFiscal(data)
    } catch {
      toast.error("Raport fiscal indisponibil")
    }
  }

  const downloadHomecareExport = async (format) => {
    try {
      const { data } = await axios.get(`${API}/reports/homecare-hours`, {
        params: { month: fiscalMonth, format },
        responseType: "blob",
      })
      const mime = format === "pdf" ? "application/pdf" : "text/csv"
      const blob = new Blob([data], { type: mime })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = format === "pdf" ? `homecare-${fiscalMonth}.pdf` : `homecare-${fiscalMonth}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast.success("Export descărcat")
    } catch {
      toast.error("Export indisponibil")
    }
  }

  const loadAudit = async () => {
    try {
      const { data } = await axios.get(`${API}/compliance/audit-log`, { params: { limit: 150 } })
      setAudit(Array.isArray(data) ? data : [])
      setActive("security")
    } catch {
      toast.error("Audit indisponibil")
    }
  }

  if (!settings) {
    return <AppLayout title={t("app.settings.title")}><p className="text-muted-foreground">{t("app.settings.loading")}</p></AppLayout>
  }

  const { profile, practice, clinical, notifications, security, subscription, ai_saved_minutes } = settings
  const canEditPracticeClinical = ["owner", "admin"].includes(profile?.role)
  const enabledVerticals = clinical?.enabled_verticals || ["psych"]
  const primaryVertical = clinical?.primary_vertical || "psych"

  const effectivePreview = clinicalDraft
    ? (clinicalDraft.clinical_specialty && clinicalDraft.enabled_verticals.includes(clinicalDraft.clinical_specialty)
        ? clinicalDraft.clinical_specialty
        : clinicalDraft.primary_vertical)
    : primaryVertical

  const saveClinicalProfile = async () => {
    if (!clinicalDraft) return
    setClinicalSaving(true)
    try {
      await axios.patch(`${API}/settings`, {
        profile: { clinical_specialty: clinicalDraft.clinical_specialty || null },
        ...(canEditPracticeClinical
          ? {
              clinical: {
                primary_vertical: clinicalDraft.primary_vertical,
                enabled_verticals: clinicalDraft.enabled_verticals,
              },
            }
          : {}),
      })
      const working =
        clinicalDraft.clinical_specialty && clinicalDraft.enabled_verticals.includes(clinicalDraft.clinical_specialty)
          ? clinicalDraft.clinical_specialty
          : clinicalDraft.primary_vertical
      setSessionVertical(working)
      window.dispatchEvent(new CustomEvent("asistentpersonal-clinical-settings-updated"))
      toast.success("Specialitate salvată")
      load()
    } catch {
      toast.error("Eroare la salvare")
    } finally {
      setClinicalSaving(false)
    }
  }

  const togglePracticeVertical = (value) => {
    setClinicalDraft((d) => {
      if (!d) return d
      const has = d.enabled_verticals.includes(value)
      const next = has ? d.enabled_verticals.filter((x) => x !== value) : [...d.enabled_verticals, value]
      if (!next.length) {
        toast.error("Păstrează cel puțin o specialitate")
        return d
      }
      let primary_vertical = d.primary_vertical
      if (!next.includes(primary_vertical)) primary_vertical = next[0]
      let clinical_specialty = d.clinical_specialty
      if (clinical_specialty && !next.includes(clinical_specialty)) clinical_specialty = ""
      return { ...d, enabled_verticals: next, primary_vertical, clinical_specialty }
    })
  }

  return (
    <AppLayout title={t("app.settings.title")}>
      <div className="mb-6 rounded-xl border border-accent/30 bg-accent/5 p-4 text-sm">
        AI a economisit ~{ai_saved_minutes} minute luna aceasta pe documentare.
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <nav className="space-y-0.5" data-testid="settings-nav">
            {tabs.map((tabItem) => (
              <button
                key={tabItem.id}
                onClick={() => setActive(tabItem.id)}
                data-testid={`settings-tab-${tabItem.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active === tabItem.id ? "bg-secondary text-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <tabItem.icon strokeWidth={1.5} className="h-4 w-4" />
                {tabItem.label}
              </button>
            ))}
          </nav>
        </aside>

        <section className="lg:col-span-9 space-y-12">
          {active === "profile" && (
            <>
            <SettingSection eyebrow="Profil" title="Contul tău" description="Informații afișate pe documente.">
              <Row label="Nume complet">
                <Input defaultValue={profile.name} className="max-w-md rounded-md" onBlur={(e) => save({ profile: { name: e.target.value } })} />
              </Row>
              <Row label="Titlu">
                <Input defaultValue={profile.title} className="max-w-md rounded-md" onBlur={(e) => save({ profile: { title: e.target.value } })} />
              </Row>
              <Row label="Email"><Input defaultValue={profile.email} disabled className="max-w-md rounded-md" /></Row>
              <Row label="Telefon">
                <Input defaultValue={profile.phone} className="max-w-md rounded-md" onBlur={(e) => save({ profile: { phone: e.target.value } })} />
              </Row>
            </SettingSection>

            <div id="clinical-specialty">
            <SettingSection
              eyebrow="Clinic"
              title="Specialitate clinică"
              description="Definește cu ce specialitate lucrezi și ce șabloane vezi în Note, Planuri îngrijire și fișa clientului."
            >
              {!clinicalDraft && <p className="text-sm text-muted-foreground">Se încarcă setările de specialitate…</p>}
              {clinicalDraft && (
              <>
              <div className="rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 text-sm max-w-lg mb-2">
                Activ acum: <span className="font-medium">{labelForVertical(effectivePreview)}</span>
              </div>
              <Row label="Lucrez ca">
                <div className="max-w-md space-y-1">
                  <select
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={clinicalDraft.clinical_specialty}
                    onChange={(e) => setClinicalDraft((d) => ({ ...d, clinical_specialty: e.target.value }))}
                  >
                    <option value="">— cabinet ({labelForVertical(clinicalDraft.primary_vertical)}) —</option>
                    {CLINICAL_VERTICALS.filter((v) => clinicalDraft.enabled_verticals.includes(v.value)).map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">Specialitatea ta de practician în aplicație.</p>
                </div>
              </Row>
              {canEditPracticeClinical && (
                <>
                  <Row label="Cabinet — principală">
                    <select
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm max-w-md"
                      value={clinicalDraft.primary_vertical}
                      onChange={(e) => {
                        const pv = e.target.value
                        setClinicalDraft((d) => ({
                          ...d,
                          primary_vertical: pv,
                          enabled_verticals: d.enabled_verticals.includes(pv) ? d.enabled_verticals : [pv, ...d.enabled_verticals],
                        }))
                      }}
                    >
                      {CLINICAL_VERTICALS.map((v) => (
                        <option key={v.value} value={v.value}>{v.label}</option>
                      ))}
                    </select>
                  </Row>
                  <Row label="Cabinet — servicii">
                    <div className="space-y-2 max-w-md rounded-lg border border-border p-3 bg-background">
                      {CLINICAL_VERTICALS.map((v) => (
                        <label key={v.value} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={clinicalDraft.enabled_verticals.includes(v.value)}
                            onChange={() => togglePracticeVertical(v.value)}
                          />
                          {v.label}
                        </label>
                      ))}
                    </div>
                  </Row>
                  <Row label="Șabloane note">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={async () => {
                        try {
                          const { data } = await axios.post(`${API}/note-templates/seed`, {
                            verticals: clinicalDraft.enabled_verticals,
                          })
                          toast.success(`Șabloane adăugate: ${data.inserted ?? 0}`)
                        } catch {
                          toast.error("Încărcare șabloane eșuată")
                        }
                      }}
                    >
                      Reîncarcă șabloane pentru specialitățile bifate
                    </Button>
                  </Row>
                  <Row label="Planuri tratament">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={async () => {
                        try {
                          const { data } = await axios.post(`${API}/care-plan-templates/seed`, {
                            modalities: clinicalDraft.enabled_verticals,
                          })
                          toast.success(
                            `Bibliotecă: ${data.library_seeded ?? 0}, șabloane sistem: ${data.system_templates ?? 0}, fork: ${data.practice_forks ?? 0}`
                          )
                        } catch {
                          toast.error("Încărcare șabloane plan eșuată")
                        }
                      }}
                    >
                      Reîncarcă șabloane plan tratament
                    </Button>
                  </Row>
                </>
              )}
              {!canEditPracticeClinical && (
                <p className="text-sm text-muted-foreground max-w-lg">
                  Cabinetul oferă: {clinicalDraft.enabled_verticals.map((v) => labelForVertical(v)).join(", ")}.
                  Doar administratorul poate modifica lista; tu poți seta „Lucrez ca”.
                </p>
              )}
              <div className="pt-2">
                <Button type="button" className="rounded-full" onClick={saveClinicalProfile} disabled={clinicalSaving}>
                  {clinicalSaving ? "Se salvează…" : "Salvează specialitatea"}
                </Button>
              </div>
              </>
              )}
            </SettingSection>
            </div>
            </>
          )}

          {active === "employer" && (
            <SettingSection eyebrow="Angajator" title="Profil angajator HR" description="PFA, SRL, II sau ONG — folosit la contracte și REGES.">
              <Row label="Formă juridică">
                <select
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm max-w-md"
                  value={employer.legal_form}
                  onChange={(e) => setEmployer({ ...employer, legal_form: e.target.value })}
                >
                  <option value="pfa">PFA</option>
                  <option value="srl">SRL</option>
                  <option value="ii">II</option>
                  <option value="ong">ONG</option>
                </select>
              </Row>
              <Row label="CAEN">
                <Input className="max-w-md" value={employer.caen} onChange={(e) => setEmployer({ ...employer, caen: e.target.value })} />
              </Row>
              <Row label="Reprezentant legal">
                <Input className="max-w-md" value={employer.delegated_person} onChange={(e) => setEmployer({ ...employer, delegated_person: e.target.value })} />
              </Row>
              <Row label="Nr. înregistrare ITM">
                <Input className="max-w-md" value={employer.itm_registration} onChange={(e) => setEmployer({ ...employer, itm_registration: e.target.value })} />
              </Row>
              <Button className="rounded-full" onClick={async () => {
                try {
                  await axios.patch(`${API}/hr/employer-profile`, employer)
                  toast.success("Profil angajator salvat")
                } catch {
                  toast.error("Salvare eșuată")
                }
              }}>Salvează profil angajator</Button>
            </SettingSection>
          )}

          {active === "practice" && (
            <SettingSection eyebrow="Cabinet" title="Date cabinet" description="Folosite pe facturi și e-Factura.">
              <Row label="Nume cabinet">
                <Input defaultValue={practice.name} className="max-w-md rounded-md" onBlur={(e) => save({ practice: { name: e.target.value } })} />
              </Row>
              <Row label="Adresă">
                <Input defaultValue={practice.address} className="max-w-md rounded-md" onBlur={(e) => save({ practice: { address: e.target.value } })} />
              </Row>
              <Row label="CUI">
                <Input defaultValue={practice.cui} className="max-w-md rounded-md" onBlur={(e) => save({ practice: { cui: e.target.value } })} />
              </Row>
              <Row label="Denumire legală (factură / UBL)">
                <Input defaultValue={practice.supplier_legal_name || ""} className="max-w-md rounded-md" placeholder="Opțional — dacă diferă de numele cabinetului" onBlur={(e) => save({ practice: { supplier_legal_name: e.target.value } })} />
              </Row>
              <Row label="IBAN">
                <Input defaultValue={practice.iban || ""} className="max-w-md rounded-md font-mono text-sm" onBlur={(e) => save({ practice: { iban: e.target.value } })} />
              </Row>
              <Row label="Bancă">
                <Input defaultValue={practice.bank_name || ""} className="max-w-md rounded-md" onBlur={(e) => save({ practice: { bank_name: e.target.value } })} />
              </Row>
              <Row label="Plătitor TVA">
                <Switch checked={!!practice.vat_registered} onCheckedChange={(v) => save({ practice: { vat_registered: v } })} />
              </Row>
              <Row label="Reg. Com.">
                <Input defaultValue={practice.reg_com} className="max-w-md rounded-md" onBlur={(e) => save({ practice: { reg_com: e.target.value } })} />
              </Row>
              <Row label="Slug rezervări">
                <Input defaultValue={practice.slug} className="max-w-md rounded-md font-mono text-sm" onBlur={(e) => save({ practice: { slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") } })} />
              </Row>
              <Row label="Link public">
                <p className="text-sm text-muted-foreground break-all">
                  {typeof window !== "undefined" ? `${window.location.origin}/book/${practice.slug || "cabinet"}` : ""}
                </p>
              </Row>
              <Row label="Remindere programări">
                <div className="flex flex-wrap gap-2 items-center">
                  <Button variant="outline" className="rounded-full" onClick={runReminders}>
                    {t("app.settings.runReminders")}
                  </Button>
                  <Button variant="ghost" className="rounded-full" asChild>
                    <Link to="/app/reminder-rules">{t("app.nav.reminderRules")}</Link>
                  </Button>
                </div>
              </Row>
              {analytics && (
                <div className="rounded-xl border border-border p-4 text-sm">
                  <p className="flex items-center gap-2 font-medium"><BarChart3 className="h-4 w-4" /> Utilizare: {analytics.utilization_pct}%</p>
                  <p className="text-muted-foreground mt-1">{analytics.benchmark}</p>
                </div>
              )}
              <Button variant="ghost" size="sm" className="rounded-full" onClick={loadAnalytics}>Încarcă analytics</Button>
            </SettingSection>
          )}

          {active === "notifications" && (
            <SettingSection eyebrow="Notificări" title="Când să te anunțăm" description="Email și digest zilnic.">
              <Toggle label="Programare nouă" checked={notifications.appointments} onChange={(v) => save({ notifications: { appointments: v } })} />
              <Toggle label="Anulare în 24h" checked={notifications.cancellations} onChange={(v) => save({ notifications: { cancellations: v } })} />
              <Toggle label="Note nesemnate (zilnic)" checked={notifications.note_digest} onChange={(v) => save({ notifications: { note_digest: v } })} />
              <Toggle label="Sumar venituri săptămânal" checked={notifications.revenue_weekly} onChange={(v) => save({ notifications: { revenue_weekly: v } })} />
            </SettingSection>
          )}

          {active === "billing" && (
            <SettingSection eyebrow="Abonament" title="Plan și facturare" description="Solo, Practice sau Clinic. Probă 30 zile cu toate funcțiile.">
              <div className="rounded-2xl border border-border p-6 max-w-xl space-y-4">
                <PilotBanner>
                  Plățile Stripe necesită chei API configurate pe server. Fără Stripe, checkout-ul returnează mod demonstrativ.
                </PilotBanner>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">Plan efectiv</p>
                  <p className="font-display text-2xl tracking-tight font-medium mt-1 capitalize">
                    {subscription.effective_plan || subscription.plan}
                    {subscription.status === "trialing" && " (probă)"}
                  </p>
                  <p className="text-xs mt-1">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full capitalize ${
                        subscription.status === "past_due" || subscription.status === "unpaid"
                          ? "bg-destructive/15 text-destructive"
                          : subscription.status === "active"
                            ? "bg-secondary"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {subscription.status || "—"}
                    </span>
                    {subscription.billing_interval && (
                      <span className="ml-2 text-muted-foreground">
                        · {subscription.billing_interval === "yearly" ? "anual" : "lunar"}
                      </span>
                    )}
                  </p>
                  {subscription.trial_ends_at && (
                    <p className="text-sm text-muted-foreground mt-1">Probă până: {String(subscription.trial_ends_at).slice(0, 10)}</p>
                  )}
                  {subscription.grace_ends_at && (subscription.status === "past_due" || subscription.status === "unpaid") && (
                    <p className="text-sm text-destructive mt-1">
                      Grație plată până: {String(subscription.grace_ends_at).slice(0, 10)}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground mt-1">
                    Locuri: {subscription.seat_used ?? "—"} / {subscription.seat_limit ?? 1}
                  </p>
                </div>
                <div className="inline-flex p-1 rounded-full border border-border bg-card">
                  {["monthly", "yearly"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setBillingInterval(m)}
                      className={`px-4 py-1 rounded-full text-xs ${
                        billingInterval === m ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {m === "monthly" ? "Lunar" : "Anual"}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {["solo", "practice", "clinic"].map((p) => (
                    <Button key={p} variant="outline" size="sm" className="rounded-full capitalize" onClick={() => stripeCheckout(p)}>
                      Upgrade {p}
                    </Button>
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button className="rounded-full" onClick={billingPortal}>Portal Stripe</Button>
                  <Button variant="ghost" className="rounded-full" onClick={() => stripeCheckout((subscription.subscription_plan || "practice").toLowerCase())}>
                    Checkout Stripe
                  </Button>
                  <Button variant="outline" className="rounded-full" onClick={loadSaasInvoices}>
                    Facturi abonament
                  </Button>
                </div>
                {saasInvoices.length > 0 && (
                  <div className="border border-border rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs uppercase text-muted-foreground bg-muted/40">
                          <th className="text-left px-3 py-2">Dată</th>
                          <th className="text-left px-3 py-2">Sumă</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-right px-3 py-2">PDF</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {saasInvoices.map((inv) => (
                          <tr key={inv.id}>
                            <td className="px-3 py-2 text-muted-foreground">
                              {inv.created ? new Date(inv.created * 1000).toISOString().slice(0, 10) : "—"}
                            </td>
                            <td className="px-3 py-2">
                              {inv.amount_paid ?? inv.amount_due} {inv.currency}
                            </td>
                            <td className="px-3 py-2 capitalize">{inv.status}</td>
                            <td className="px-3 py-2 text-right">
                              {inv.invoice_pdf ? (
                                <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-accent underline text-xs">
                                  PDF
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </SettingSection>
          )}

          {active === "security" && (
            <SettingSection eyebrow="Securitate" title="Protecție date" description="GDPR și audit.">
              <div className="pt-5 space-y-3">
                <p className="text-sm font-medium">Autentificare 2FA (TOTP)</p>
                {!security.two_factor && !totpSecret && (
                  <Button variant="outline" className="rounded-full" onClick={setup2fa}>Configurează 2FA</Button>
                )}
                {totpSecret && !security.two_factor && (
                  <div className="space-y-2 max-w-md text-xs">
                    <p className="font-mono break-all text-muted-foreground">{totpUri}</p>
                    <Input placeholder="Cod 6 cifre" value={totpCode} onChange={(e) => setTotpCode(e.target.value)} className="max-w-xs" />
                    <Button className="rounded-full" onClick={verify2fa}>Activează</Button>
                  </div>
                )}
                {security.two_factor && (
                  <div className="space-y-2 max-w-md">
                    <p className="text-xs text-muted-foreground">2FA activ. Introdu cod pentru dezactivare.</p>
                    <Input placeholder="Cod 6 cifre" value={totpCode} onChange={(e) => setTotpCode(e.target.value)} className="max-w-xs" />
                    <Button variant="destructive" className="rounded-full" onClick={disable2fa}>Dezactivează 2FA</Button>
                  </div>
                )}
                {backupCodes.length > 0 && (
                  <div className="text-xs p-3 rounded-lg bg-secondary">
                    <p className="font-semibold mb-1">Coduri backup (salvează acum):</p>
                    {backupCodes.map((c) => <p key={c} className="font-mono">{c}</p>)}
                  </div>
                )}
              </div>
              <Toggle label="Parole puternice" checked={security.strong_passwords} onChange={(v) => save({ security: { strong_passwords: v } })} />
              <Toggle label="Deconectare după 30 min" checked={security.idle_timeout} onChange={(v) => save({ security: { idle_timeout: v } })} />
              <Row label="Raport fiscal (lună)">
                <div className="flex flex-wrap gap-2 items-center">
                  <Input type="month" value={fiscalMonth} onChange={(e) => setFiscalMonth(e.target.value)} className="w-40 rounded-md" />
                  <Button variant="outline" className="rounded-full" type="button" onClick={loadFiscal}>Încarcă</Button>
                </div>
              </Row>
              {fiscal && (
                <div className="text-sm rounded-lg border border-border p-3 max-w-md space-y-1">
                  <p><span className="text-muted-foreground">Lună:</span> {fiscal.month}</p>
                  <p><span className="text-muted-foreground">Încasat (facturi plătite):</span> {fiscal.paid_total_ron?.toFixed?.(2) ?? fiscal.paid_total_ron} RON</p>
                  <p><span className="text-muted-foreground">Neîncasat:</span> {fiscal.unpaid_total_ron?.toFixed?.(2) ?? fiscal.unpaid_total_ron} RON</p>
                  <p><span className="text-muted-foreground">Nr. facturi:</span> {fiscal.invoice_count}</p>
                  <p className="text-[10px] text-muted-foreground">Sumar informativ — nu constituie sfat fiscal sau contabil.</p>
                </div>
              )}
              <Row label="Ore vizite domiciliu (APP / pilot)">
                <div className="flex flex-col gap-2 max-w-md">
                  <PilotBanner>
                    Export pilot APP / Legea 448 — validează cu contabil și DGASPC înainte de utilizare oficială.
                  </PilotBanner>
                  <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-muted-foreground max-w-xs">
                    Folosește aceeași lună ca mai sus. Include coloană „legal review”. PDF/CSV — validează cu contabil/DGASPC.
                  </span>
                  <Button variant="outline" className="rounded-full text-xs" type="button" onClick={() => downloadHomecareExport("csv")}>CSV</Button>
                  <Button variant="outline" className="rounded-full text-xs" type="button" onClick={() => downloadHomecareExport("pdf")}>PDF</Button>
                </div>
                </div>
              </Row>
              <Row label="Export date (GDPR)">
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="rounded-full" onClick={exportData}>Export rapid</Button>
                  <Button variant="outline" className="rounded-full" onClick={exportFull}>Pachet complet</Button>
                </div>
              </Row>
              <Row label="Jurnal audit">
                <Button variant="outline" className="rounded-full" onClick={loadAudit}>Deschide jurnal</Button>
              </Row>
              {audit.length > 0 && (
                <ul className="text-xs space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                  {audit.map((a) => (
                    <li key={a.id}>{a.created_at?.slice(0, 16)} — {a.action} {a.entity_type}</li>
                  ))}
                </ul>
              )}
              <Row label="Checklist conformitate">
                <Button variant="outline" className="rounded-full" onClick={loadCompliance}>Verifică</Button>
              </Row>
              {compliance.length > 0 && (
                <ul className="text-sm space-y-2">
                  {compliance.map((c) => (
                    <li key={c.id} className="flex items-center gap-2">
                      <span className={c.done ? "text-accent" : "text-muted-foreground"}>{c.done ? "✓" : "○"}</span>
                      {c.label}
                    </li>
                  ))}
                </ul>
              )}
              <div className="pt-5 border-t">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" /> Invită coleg
                </p>
                <p className="text-xs text-muted-foreground mb-2">
                  Locuri: {subscription.seat_used ?? "—"} / {subscription.seat_limit ?? 1}
                </p>
                {(subscription.seat_used ?? 0) >= (subscription.seat_limit ?? 1) && (
                  <p className="text-sm text-destructive mb-3">
                    Limita de locuri atinsă.{" "}
                    <button type="button" className="underline" onClick={() => setActive("billing")}>
                      Upgrade plan
                    </button>
                  </p>
                )}
                <div className="space-y-2 max-w-md">
                  <Input placeholder="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
                  <Input placeholder="Nume" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
                  <Button
                    className="rounded-full"
                    onClick={sendInvite}
                    disabled={(subscription.seat_used ?? 0) >= (subscription.seat_limit ?? 1)}
                  >
                    Generează link
                  </Button>
                </div>
              </div>
              <div className="pt-5 border-t">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">White-label</p>
                <Input className="max-w-md mb-2" placeholder="Logo URL" value={branding.logo_url} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} />
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-muted-foreground hover:text-foreground mb-2">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
                  Încarcă logo (Supabase Storage)
                </label>
                <Input className="max-w-md mb-2" type="color" value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} />
                <Button variant="outline" className="rounded-full" onClick={saveBranding}>Salvează branding</Button>
              </div>
            </SettingSection>
          )}

          {active === "integrations" && (
            <SettingSection eyebrow="Integrări" title="Conectează uneltele" description="Calendar, plăți, telehealth.">
              <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                {integrations.map((int) => (
                  <div key={int.provider} className="rounded-2xl border border-border p-5 flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium capitalize">{int.provider.replace("_", " ")}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {int.status === "connected" ? "Conectat" : int.status === "needs_config" ? "Necesită configurare" : int.status}
                      </p>
                      {int.status_hint && int.status !== "connected" && (
                        <p className="text-[10px] text-muted-foreground mt-1 max-w-xs">{int.status_hint}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={int.status === "connected" ? "secondary" : "outline"}
                      className="rounded-full shrink-0"
                      onClick={() => connect(int.provider, int.connectable, int.status_hint)}
                      disabled={int.status === "connected"}
                    >
                      {int.status === "connected" ? "Conectat" : int.connectable ? "Conectează" : "Configurare"}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="mt-10 max-w-xl space-y-3 pt-6 border-t border-border">
                <p className="text-sm font-medium">Telehealth</p>
                <p className="text-xs text-muted-foreground">
                  Jitsi integrat generează automat camere video la programările Telehealth. Whereby/Zoom/Daily rămân opționale pentru link extern.
                </p>
                <select
                  className="flex h-10 w-full max-w-xs rounded-md border border-input bg-background px-3 text-sm"
                  value={teleProvider}
                  onChange={(e) => setTeleProvider(e.target.value)}
                >
                  <option value="jitsi">Jitsi (integrat)</option>
                  <option value="whereby">Whereby (link extern)</option>
                  <option value="zoom">Zoom (link extern)</option>
                  <option value="daily">Daily.co (link extern)</option>
                </select>
                {teleProvider !== "jitsi" && (
                  <Input placeholder="https://… (room sau subdomain)" value={teleUrl} onChange={(e) => setTeleUrl(e.target.value)} className="max-w-lg" />
                )}
                <Button
                  variant="outline"
                  className="rounded-full"
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.patch(`${API}/integrations/telehealth`, {
                        provider: teleProvider,
                        room_base_url: teleUrl || null,
                      })
                      toast.success("Telehealth salvat")
                    } catch {
                      toast.error("Salvare eșuată")
                    }
                  }}
                >
                  Salvează telehealth
                </Button>
              </div>
            </SettingSection>
          )}
        </section>
      </div>
    </AppLayout>
  )
}

function SettingSection({ eyebrow, title, description, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{eyebrow}</p>
      <h2 className="font-display text-2xl md:text-3xl tracking-tight font-medium mt-2">{title}</h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-xl">{description}</p>
      <div className="mt-8 space-y-5 divide-y divide-border">{children}</div>
    </div>
  )
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 pt-5 items-start">
      <Label className="text-sm pt-2">{label}</Label>
      <div className="sm:col-span-2">{children}</div>
    </div>
  )
}

function Toggle({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between pt-5">
      <Label className="text-sm">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  )
}
